import {
    Drawer,
    DrawerOverlay,
    DrawerContent,
    Box,
    Text,
    VStack,
    HStack,
    Flex,
    Icon,
    CloseButton,
    Input,
    InputGroup,
    InputLeftElement,
    Spinner,
    Center,
    Button,
    Menu,
    MenuButton,
    MenuList,
    Badge,
    Tooltip,
} from '@chakra-ui/react';
import React, { useState, useMemo, useEffect } from 'react';
import { TbSearch, TbTag, TbFilter, TbBellOff } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';
import { useGetNotifications, isNotificationTypeMuted, NotificationItem, NotificationType } from '../../../hooks/useGetNotifications';
import { useMarkNotificationRead } from '../../../hooks/useMarkNotificationRead';
import { useMarkNegotiationRead } from '../../../hooks/invoice/useInvoiceNegotiation';
import { invoicesListHref } from '../../../shared/invoicesListViewState';
import { useForceSidebarCollapsed } from '../../../contexts/SidebarCollapseOverrideContext';
import {
    getNotificationIcon,
    getNotificationItemMessage,
    getNotificationTag,
} from '../../../components/notifications/NotificationRow';
import { SIDE_DRAWER_CONTAINER_PROPS, INSTANT_SIDE_DRAWER_MOTION_PROPS } from '../../../modals/sideDrawerContainerProps';
import Timeline, { TimelineGroupData } from '../../../components/Timeline';
import { MenuItemRow } from '../../../ui';

type OrderStep = 'newParts' | 'quoting' | 'selection' | 'ordered' | 'rekey';

interface OrderNotificationsDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    orderId: string;
    hideOverlay?: boolean;
    /** Switch the order page's active tab when a notification is clicked. */
    onSelectTab?: (status: OrderStep) => void;
}

type ReadFilter = 'all' | 'unread' | 'read';
type TypeFilter = 'all' | NotificationType;

const READ_FILTER_LABELS: Record<ReadFilter, string> = {
    all: 'All',
    unread: 'Unread',
    read: 'Read',
};

/** Mirrors the mapping in `NotificationDrawer` so a clicked notification lands
 *  on the most relevant tab of the order it belongs to. */
const getInitialStatusForNotification = (type: NotificationType): OrderStep => {
    switch (type) {
        case 'new-order':
            return 'newParts';
        case 'quotes-extended':
        case 'auto-requote':
            return 'quoting';
        case 'quotes-back':
        case 'quotes-back-potential-total':
        case 'quotes-back-quotes-only':
        case 'no-quoted-parts':
        case 'flagged-parts':
        case 'part-number-changed':
        case 'pre-order-backorder':
        case 'order-request-enabled':
            return 'selection';
        case 'orders-confirmed':
        case 'orders-rejected':
        case 'orders-returned-cancelled':
        case 'tesla-order-error':
        case 'order-not-confirmed':
        case 'supplier-changes':
        case 'order-placement-failed':
        case 'ccc-manual-po-created':
        case 'ccc-manual-po-failed':
        case 'inventory-stock-request-placed':
            return 'ordered';
        case 'rekeying-completed':
        case 'rekeying-completed-flagged':
        case 'rekeying-skipped':
        case 'rekey-quote-only-completed':
        case 'needs-rekey':
            return 'rekey';
        default:
            return 'newParts';
    }
};

function formatDateGroup(date: Date): string {
    const d = new Date(date);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const itemDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());

    if (itemDate.getTime() === today.getTime()) return 'Today';

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (itemDate.getTime() === yesterday.getTime()) return 'Yesterday';

    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function formatTime(date: Date): string {
    return new Date(date).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });
}

function groupByDate(items: NotificationItem[]): Map<string, NotificationItem[]> {
    const groups = new Map<string, NotificationItem[]>();
    for (const item of items) {
        const key = formatDateGroup(item.timeCreated);
        const group = groups.get(key);
        if (group) {
            group.push(item);
        } else {
            groups.set(key, [item]);
        }
    }
    return groups;
}

const OrderNotificationsDrawer: React.FC<OrderNotificationsDrawerProps> = ({
    isOpen,
    onClose,
    orderId,
    hideOverlay = false,
    onSelectTab,
}) => {
    useForceSidebarCollapsed(isOpen);
    const navigate = useNavigate();
    const markAsRead = useMarkNotificationRead();
    const markNegotiationRead = useMarkNegotiationRead();

    const [search, setSearch] = useState('');
    const [readFilter, setReadFilter] = useState<ReadFilter>('all');
    const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
    const [offset, setOffset] = useState(0);
    // Accumulated pages keyed by their request offset. Keying by offset (rather
    // than blindly appending) means a refetch of an already-loaded page — from the
    // 10s poll or a cache invalidation after marking-as-read — *replaces* that page
    // instead of duplicating its rows into the list.
    const [pages, setPages] = useState<Record<number, NotificationItem[]>>({});

    const { data, isLoading, isError, isFetching, refetch } = useGetNotifications(
        isOpen && !!orderId,
        undefined,
        offset,
        undefined,
        orderId,
    );

    // Reset filters/pagination whenever the drawer opens.
    useEffect(() => {
        if (isOpen) {
            setSearch('');
            setReadFilter('all');
            setTypeFilter('all');
            setOffset(0);
            setPages({});
        }
    }, [isOpen, orderId]);

    // Store each fetched page under its offset. Re-fetching the same offset (poll
    // or invalidation) overwrites that page rather than appending a duplicate.
    useEffect(() => {
        if (isOpen && data?.notifications) {
            setPages((prev) => ({ ...prev, [offset]: data.notifications }));
        }
    }, [isOpen, data?.notifications, offset]);

    // Flatten pages in offset order into a single de-duplicated list. De-duping by
    // `_id` also guards against the same notification straddling two pages when a
    // new one arrives and shifts the pagination window.
    const allNotifications = useMemo<NotificationItem[]>(() => {
        const seen = new Set<string>();
        const result: NotificationItem[] = [];
        Object.keys(pages)
            .map(Number)
            .sort((a, b) => a - b)
            .forEach((key) => {
                for (const n of pages[key]) {
                    if (!seen.has(n._id)) {
                        seen.add(n._id);
                        result.push(n);
                    }
                }
            });
        return result;
    }, [pages]);

    const availableTypeFilters = useMemo<TypeFilter[]>(() => {
        const present = Array.from(new Set(allNotifications.map((n) => n.type)));
        return ['all', ...present];
    }, [allNotifications]);

    const filteredNotifications = useMemo(() => {
        const q = search.trim().toLowerCase();
        return allNotifications.filter((n) => {
            if (readFilter === 'unread' && n.read) return false;
            if (readFilter === 'read' && !n.read) return false;
            if (typeFilter !== 'all' && n.type !== typeFilter) return false;
            if (!q) return true;
            const message = getNotificationItemMessage(n).toLowerCase();
            const tag = getNotificationTag(n.type).label.toLowerCase();
            const vehicle = n.preview?.vehicle?.toLowerCase() ?? '';
            const ro = n.preview?.roNumber?.toLowerCase() ?? '';
            const sender = n.senderName?.toLowerCase() ?? '';
            return (
                message.includes(q) ||
                tag.includes(q) ||
                vehicle.includes(q) ||
                ro.includes(q) ||
                sender.includes(q)
            );
        });
    }, [allNotifications, search, readFilter, typeFilter]);

    const grouped = useMemo(() => groupByDate(filteredNotifications), [filteredNotifications]);

    const hasTypeFilter = typeFilter !== 'all';
    const hasReadFilter = readFilter !== 'all';

    // Mark a notification read with an immediate local (optimistic) update so the
    // blue dot disappears the instant it's clicked. The shared mutation's own
    // optimistic update only targets the global `['/notifications']` cache key, so
    // without this the order-scoped list would lag until the request resolved.
    const markRead = (notification: NotificationItem) => {
        if (notification.read) return;
        setPages((prev) => {
            const next: Record<number, NotificationItem[]> = {};
            for (const [key, items] of Object.entries(prev)) {
                next[Number(key)] = items.map((n) =>
                    n._id === notification._id ? { ...n, read: true } : n,
                );
            }
            return next;
        });
        markAsRead.mutate({ notificationId: notification._id });
    };

    // Clicking a notification marks it read and surfaces the relevant tab, but
    // intentionally leaves the drawer open — opening/closing is a discrete action
    // handled by the header close button (and the sidebar "View All" entry point).
    const handleNotificationClick = (notification: NotificationItem) => {
        markRead(notification);

        if (notification.data.type === 'negotiation') {
            markNegotiationRead.mutate(notification.data.negotiationId);
            navigate(
                invoicesListHref({
                    type: 'all',
                    showArchived: false,
                    showNegotiation: true,
                    selectedNegotiationId: notification.data.negotiationId,
                    currentPage: 1,
                }),
                { state: { source: 'notifications' } },
            );
            onClose();
        } else if (notification.data.type === 'invoice') {
            // Flagged-invoice notifications are surfaced here (matched to the order by
            // RO number) but always deep-link to the invoice itself.
            navigate(`/invoices/${notification.data.invoiceId}`, {
                state: { source: 'notifications' },
            });
            onClose();
        } else if (
            notification.type === 'inventory-stock-request' ||
            notification.data.type === 'stock-request' ||
            notification.data.type === 'purchase-order'
        ) {
            const purchaseOrderId =
                notification.data.type === 'purchase-order'
                    ? notification.data.purchaseOrderId
                    : notification.data.type === 'stock-request'
                        ? notification.data.stockRequestId
                        : undefined;
            const qs = purchaseOrderId
                ? `?purchaseOrderId=${encodeURIComponent(purchaseOrderId)}`
                : '';
            navigate(`/inventory/purchase-orders${qs}`, {
                state: { source: 'notifications' },
            });
            onClose();
        } else if (notification.data.type === 'order') {
            if (onSelectTab) {
                onSelectTab(getInitialStatusForNotification(notification.type));
            } else {
                navigate(`/order/${notification.data.orderId}`, {
                    state: {
                        source: 'notifications',
                        initialStatus: getInitialStatusForNotification(notification.type),
                    },
                });
            }
        }
    };

    const renderNotificationCard = (notification: NotificationItem) => {
        const { icon, color, bg } = getNotificationIcon(notification.type);
        const tag = getNotificationTag(notification.type);
        const preview = notification.preview;
        // Muted types never toast or appear in the bell drawer, but are shown here
        // as part of the order's audit trail (ENG-2136) — label them so it's clear
        // why no alert fired.
        const muted = isNotificationTypeMuted(
            notification.type,
            data?.notificationsEnabled,
            data?.enabledNotificationTypes,
        );
        return (
            <Box
                as="button"
                textAlign="left"
                w="100%"
                borderWidth="1px"
                borderColor={notification.read ? 'gray.200' : 'rgba(0, 113, 227, 0.35)'}
                borderRadius="lg"
                bg={notification.read ? 'white' : 'rgba(0, 113, 227, 0.04)'}
                p={3}
                transition="border-color 0.15s, box-shadow 0.15s, background-color 0.15s"
                _hover={{ borderColor: 'gray.300', boxShadow: 'none' }}
                onClick={() => handleNotificationClick(notification)}
            >
                <Flex align="flex-start" gap={3}>
                    {/* Icon */}
                    <Flex
                        w="32px"
                        h="32px"
                        borderRadius="full"
                        bg={bg}
                        align="center"
                        justify="center"
                        flexShrink={0}
                        mt={0.5}
                    >
                        <Icon as={icon as React.ElementType} w={4} h={4} color={color} />
                    </Flex>

                    {/* Content */}
                    <Box flex={1} minW={0}>
                        <Flex align="center" justify="space-between" gap={2} minW={0}>
                            <HStack spacing={2} minW={0} flex={1}>
                                <Text
                                    fontSize="sm"
                                    fontWeight={notification.read ? 'medium' : 'semibold'}
                                    color="gray.800"
                                    isTruncated
                                    minW={0}
                                    lineHeight="1.3"
                                >
                                    {getNotificationItemMessage(notification)}
                                </Text>
                                <Badge
                                    flexShrink={0}
                                    fontSize="9px"
                                    px={1.5}
                                    py={0.5}
                                    borderRadius="md"
                                    bg={tag.bg}
                                    color={tag.color}
                                    fontWeight="medium"
                                    textTransform="none"
                                >
                                    {tag.label}
                                </Badge>
                            </HStack>
                            <HStack spacing={2} flexShrink={0}>
                                <Text
                                    fontSize="xs"
                                    color="gray.500"
                                    whiteSpace="nowrap"
                                >
                                    {formatTime(notification.timeCreated)}
                                </Text>
                                {!notification.read && (
                                    <Tooltip label="Mark read" placement="top" hasArrow openDelay={300}>
                                        <Box
                                            as="span"
                                            role="button"
                                            aria-label="Mark read"
                                            w="10px"
                                            h="10px"
                                            borderRadius="full"
                                            bg="brand.solid"
                                            cursor="pointer"
                                            transition="transform 120ms ease, background-color 120ms ease"
                                            _hover={{ transform: 'scale(1.25)', bg: 'brand.hover' }}
                                            onClick={(e: React.MouseEvent) => {
                                                e.stopPropagation();
                                                markRead(notification);
                                            }}
                                        />
                                    </Tooltip>
                                )}
                            </HStack>
                        </Flex>

                        {(preview && (preview.vehicle || preview.roNumber)) || notification.senderName || muted ? (
                            <Flex align="flex-end" justify="space-between" gap={2} mt={1} minW={0}>
                                <Box flex={1} minW={0}>
                                    {preview && (preview.vehicle || preview.roNumber) && (
                                        <Text fontSize="13px" color="gray.600" noOfLines={1}>
                                            {preview.vehicle}
                                            {preview.roNumber && ` • RO ${preview.roNumber}`}
                                        </Text>
                                    )}
                                    {notification.senderName && (
                                        <Text fontSize="xs" color="gray.500" mt={0.5}>
                                            {notification.senderName}
                                        </Text>
                                    )}
                                </Box>
                                {muted && (
                                    <Tooltip
                                        label="Alerts for this type are turned off in your notification preferences"
                                        placement="top"
                                        hasArrow
                                        openDelay={300}
                                    >
                                        <Badge
                                            flexShrink={0}
                                            fontSize="9px"
                                            px={1.5}
                                            py={0.5}
                                            borderRadius="md"
                                            bg="gray.100"
                                            color="gray.500"
                                            fontWeight="medium"
                                            textTransform="none"
                                            display="inline-flex"
                                            alignItems="center"
                                            gap="3px"
                                        >
                                            <Icon as={TbBellOff as React.ElementType} boxSize={2.5} />
                                            Muted
                                        </Badge>
                                    </Tooltip>
                                )}
                            </Flex>
                        ) : null}
                    </Box>
                </Flex>
            </Box>
        );
    };

    const timelineGroups: TimelineGroupData[] = Array.from(grouped.entries()).map(([dateLabel, items]) => ({
        key: dateLabel,
        label: dateLabel,
        rightLabel: `${items.length} notification${items.length === 1 ? '' : 's'}`,
        items: items.map((notification) => ({
            key: notification._id,
            // Match the spine node ring to the notification's icon color.
            nodeColor: getNotificationIcon(notification.type).color,
            content: renderNotificationCard(notification),
        })),
    }));

    return (
        <Drawer
            isOpen={isOpen}
            placement="right"
            onClose={onClose}
            size="md"
            blockScrollOnMount={!hideOverlay}
            trapFocus={!hideOverlay}
            closeOnOverlayClick={!hideOverlay}
        >
            {!hideOverlay && <DrawerOverlay />}
            <DrawerContent
                data-partbay-drawer=""
                overflow="hidden"
                containerProps={hideOverlay ? SIDE_DRAWER_CONTAINER_PROPS : undefined}
                motionProps={INSTANT_SIDE_DRAWER_MOTION_PROPS}
            >
                <Flex direction="column" h="100%">
                    {/* Header — height locked to 64px so the bottom border lines up
                        flush with the TopBar / other side drawers. */}
                    <Flex align="center" justify="space-between" px={5} h="topbar" flexShrink={0} borderBottom="1px solid" borderColor="border.subtle" bg="white">
                        <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                            Notifications
                        </Text>
                        <CloseButton onClick={onClose} size="sm" />
                    </Flex>

                    {/* Filters */}
                    <Box px={5} py={3} borderBottom="1px solid" borderColor="gray.100">
                        <HStack spacing={2}>
                            <InputGroup size="sm" flex={1}>
                                <InputLeftElement pointerEvents="none">
                                    <Icon as={TbSearch as React.ElementType} color="gray.400" />
                                </InputLeftElement>
                                <Input
                                    placeholder="Search notifications..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    borderRadius="md"
                                    bg="gray.50"
                                    border="1px solid"
                                    borderColor="gray.200"
                                    _focus={{ borderColor: 'brand.solid', bg: 'white' }}
                                />
                            </InputGroup>

                            <Menu>
                                <MenuButton
                                    as={Button}
                                    size="sm"
                                    variant="outline"
                                    leftIcon={<Icon as={TbTag as React.ElementType} boxSize={4} />}
                                    borderColor={hasTypeFilter ? 'brand.solid' : 'gray.200'}
                                    color={hasTypeFilter ? 'brand.fg' : 'gray.700'}
                                    bg={hasTypeFilter ? 'brand.subtle' : 'gray.50'}
                                    _hover={{ bg: hasTypeFilter ? 'brand.muted' : 'gray.100' }}
                                    maxW={{ base: '130px', sm: '160px' }}
                                    flexShrink={0}
                                >
                                    <Text isTruncated>
                                        {hasTypeFilter ? getNotificationTag(typeFilter as NotificationType).label : 'All Types'}
                                    </Text>
                                </MenuButton>
                                <MenuList minW="200px" maxH="320px" overflowY="auto" p="6px">
                                    {availableTypeFilters.map((type) => (
                                        <MenuItemRow
                                            key={type}
                                            kind="single"
                                            isSelected={typeFilter === type}
                                            onClick={() => setTypeFilter(type)}
                                        >
                                            {type === 'all' ? 'All Types' : getNotificationTag(type).label}
                                        </MenuItemRow>
                                    ))}
                                </MenuList>
                            </Menu>

                            <Menu>
                                <MenuButton
                                    as={Button}
                                    size="sm"
                                    variant="outline"
                                    leftIcon={<Icon as={TbFilter as React.ElementType} boxSize={4} />}
                                    borderColor={hasReadFilter ? 'brand.solid' : 'gray.200'}
                                    color={hasReadFilter ? 'brand.fg' : 'gray.700'}
                                    bg={hasReadFilter ? 'brand.subtle' : 'gray.50'}
                                    _hover={{ bg: hasReadFilter ? 'brand.muted' : 'gray.100' }}
                                    maxW={{ base: '110px', sm: '120px' }}
                                    flexShrink={0}
                                >
                                    <Text isTruncated>{READ_FILTER_LABELS[readFilter]}</Text>
                                </MenuButton>
                                <MenuList minW="160px" p="6px">
                                    {(Object.entries(READ_FILTER_LABELS) as [ReadFilter, string][]).map(([val, label]) => (
                                        <MenuItemRow
                                            key={val}
                                            kind="single"
                                            isSelected={readFilter === val}
                                            onClick={() => setReadFilter(val)}
                                        >
                                            {label}
                                        </MenuItemRow>
                                    ))}
                                </MenuList>
                            </Menu>
                        </HStack>
                    </Box>

                    {/* Body */}
                    <Box flex={1} overflowY="auto" px={5} py={4} bg="white">
                        {isLoading && offset === 0 ? (
                            <Center py={10}>
                                <Spinner color="brand.solid" />
                            </Center>
                        ) : isError ? (
                            <Center py={10} flexDirection="column">
                                <Text fontSize="sm" color="red.500" mb={3}>
                                    Failed to load notifications
                                </Text>
                                <Button size="sm" variant="outline" colorScheme="red" onClick={() => refetch()}>
                                    Retry
                                </Button>
                            </Center>
                        ) : filteredNotifications.length === 0 ? (
                            <Center py={10}>
                                <Text fontSize="sm" color="gray.400" fontStyle="italic">
                                    No notifications for this order
                                </Text>
                            </Center>
                        ) : (
                            <VStack spacing={4} align="stretch">
                                <Timeline groups={timelineGroups} />

                                {data?.hasMore && (
                                    <Button
                                        w="100%"
                                        size="sm"
                                        variant="outline"
                                        colorScheme="blue"
                                        onClick={() => setOffset((prev) => prev + 20)}
                                        isLoading={isFetching && offset > 0}
                                    >
                                        View More
                                    </Button>
                                )}
                            </VStack>
                        )}
                    </Box>
                </Flex>
            </DrawerContent>
        </Drawer>
    );
};

export default OrderNotificationsDrawer;
